import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import * as hierarchyApi from '../api/hierarchy';

/**
 * Offices this level funds that are asking for more AI sessions.
 *
 * Their request comes here, not to the platform, because this is the pool their
 * sessions come out of. Giving moves sessions from this pool exactly as Give does, so
 * they count as handed out here and unspent ones can be taken back.
 */
export default function CreditRequestsPanel({ onAnswered }) {
  const [requests, setRequests] = useState([]);
  const [answering, setAnswering] = useState(null);   // { request, action }
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setRequests((await hierarchyApi.listCreditRequests()).requests || []);
    } catch {
      setRequests([]);      // never let this take the page around it down
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (request, action) => {
    setAnswering({ request, action });
    setAmount(request.amount_sessions ? String(request.amount_sessions) : '');
    setNote('');
  };

  const submit = async () => {
    const { request, action } = answering;
    let sessions = null;
    if (action === 'approve') {
      if (!/^\d+$/.test(amount.trim()) || Number(amount) < 1) {
        toast.error('Enter a whole number of sessions');
        return;
      }
      sessions = Number(amount);
    }
    setSaving(true);
    try {
      await hierarchyApi.answerCreditRequest(request.id, action, sessions, note.trim() || null);
      toast.success(action === 'approve'
        ? `${sessions} session${sessions === 1 ? '' : 's'} given to ${request.clinic_name}`
        : `Request from ${request.clinic_name} declined`);
      setAnswering(null);
      load();
      onAnswered?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not answer that request');
    } finally {
      setSaving(false);
    }
  };

  if (!requests.length) return null;

  return (
    <Card sx={{ mb: 3, borderColor: 'warning.main' }} variant="outlined">
      <CardContent>
        <Typography fontWeight={700} sx={{ mb: 1 }}>
          {requests.length === 1 ? '1 office is' : `${requests.length} offices are`} asking for more sessions
        </Typography>
        <Stack spacing={1.25}>
          {requests.map((r) => (
            <Box key={r.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
              p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="body2" fontWeight={600}>
                  {r.clinic_name} — {r.amount_sessions ? `${r.amount_sessions} sessions` : 'amount not given'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.requested_by ? `${r.requested_by} · ` : ''}
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                  {r.note ? ` · “${r.note}”` : ''}
                </Typography>
              </Box>
              <Button size="small" variant="contained" onClick={() => open(r, 'approve')}>Give sessions</Button>
              <Button size="small" color="inherit" onClick={() => open(r, 'decline')}>Decline</Button>
            </Box>
          ))}
        </Stack>
      </CardContent>

      <Dialog open={!!answering} onClose={() => !saving && setAnswering(null)} fullWidth maxWidth="xs">
        {answering && (
          <>
            <DialogTitle>
              {answering.action === 'approve'
                ? `Give sessions to ${answering.request.clinic_name}`
                : `Decline ${answering.request.clinic_name}'s request`}
            </DialogTitle>
            <DialogContent>
              {answering.action === 'approve' && (
                <TextField fullWidth autoFocus label="Sessions" sx={{ mt: 1 }} value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
              )}
              <TextField fullWidth multiline minRows={2} label="Note to the office (optional)"
                sx={{ mt: 2 }} value={note} onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 500 }} />
              {answering.action === 'approve' && (
                <Alert severity="info" sx={{ mt: 2 }}>Comes out of this level's pool.</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAnswering(null)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={submit} disabled={saving}
                color={answering.action === 'approve' ? 'primary' : 'error'}>
                {answering.action === 'approve' ? 'Give' : 'Decline'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Card>
  );
}
